import container from "@/lib/di/container";
import { useState, useEffect, useRef } from "react";
import { FilterBar, FilterInputTemplate, FilterInputType } from "../../components/filter/FilterBar";
import { FilterSearchPayload, DataTable } from "../../components/table/DataTable";
import { DetailModal } from "./modals/DetailModal";
import { Button, message, Tag, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { AuditLogRepo } from "@/core/repos/tables/audit-log.repo";
import { AuditLogModel } from "@/core/models/audit-log.model";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { AuditLogTableTitle } from "@/core/enums/global.enums";
import { auditLogRefFieldPipe, auditLogTitlePipe } from "@/core/pipes/audit-log-title.pipe";
import { useSearchParams } from "react-router";
import { useTitleDataStore } from "@/core/stores/title-data.store";
import { ContractRepo } from "@/core/repos/tables/contract.repo";
import { WaterInvoiceRepo } from "@/core/repos/tables/water-invoice.repo";
import { WaterIndexRepo } from "@/core/repos/tables/water-index.repo";

const auditLogRepo = container.get(AuditLogRepo);
const contractRepo = container.get(ContractRepo);
const waterInvoiceRepo = container.get(WaterInvoiceRepo);
const waterIndexRepo = container.get(WaterIndexRepo);

export default function AuditLogsPage() {
    const [searchParams] = useSearchParams();
    const search = searchParams.get('search');
    const objectType = searchParams.get('objectType');
    const titleData = useTitleDataStore();
    const [filterMap, setFilterMap] = useState<Map<string, FilterSearchPayload>>(() => {
        const initialMap = new Map<string, FilterSearchPayload>();
        if (search) {
            initialMap.set('search', { key: 'search', value: search });
        }
        if (objectType) {
            initialMap.set('title', { key: 'title', value: objectType });
        }
        return initialMap;
    })
    const [modalData, setModalData] = useState<AuditLogModel>()
    const [selectedRows, setSelectedRows] = useState<AuditLogModel[]>([]);
    const [isDetailModal, setDetailModal] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [filterTemplates, setFilterTemplates] = useState<FilterInputTemplate[]>([
        {
            dataIndex: "search",
            label: "Tìm kiếm",
            placeholder: "Nhập Tìm kiếm",
            type: FilterInputType.text,
        },
        {
            dataIndex: "title",
            type: FilterInputType.select,
            label: "Bảng tác động",
            options: Object.keys(AuditLogTableTitle).map((title) => ({
                name: auditLogTitlePipe(title),
                value: title
            }))
        },
        {
            rangeIndex: {from: "timestampFrom", to: "timestampTo"},
            type: FilterInputType.dateRange,
            placeholder: "Từ lúc",
            secondaryPlaceholder: "Đến lúc",
            label: "Thời gian tác động",
            span: 2,
            showTime: true
        }
    ])

    const hasInitializedFromParams = useRef(false);

    useEffect(() => {init()}, []);

    // Update filterMap when URL params change (only on initial mount or when params actually change)
    useEffect(() => {
        // Only update on initial mount or when search/objectType actually change
        if (!hasInitializedFromParams.current || search !== filterMap.get('search')?.value || objectType !== filterMap.get('title')?.value) {
            const newFilterMap = new Map<string, FilterSearchPayload>(filterMap);
            if (search) {
                newFilterMap.set('search', { key: 'search', value: search });
            } else if (hasInitializedFromParams.current) {
                // Only delete if we've already initialized (don't delete on initial mount if search is empty)
                newFilterMap.delete('search');
            }
            if (objectType) {
                newFilterMap.set('title', { key: 'title', value: objectType });
            } else if (hasInitializedFromParams.current) {
                newFilterMap.delete('title');
            }
            setFilterMap(newFilterMap);
            hasInitializedFromParams.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, objectType]);

    const getContractById = async (id: string) => {
        return await contractRepo.detail(id);
    }

    const getWaterInvoiceById = async (id: string) => {
        return await waterInvoiceRepo.detail(id);
    }

    const getWaterIndexById = async (id: string) => {
        return await waterIndexRepo.detail(id);
    }

    const init = async () => {
        if (search) {
            titleData.setTitleBack(true);
            if (objectType === "WaterUser") {
                const contract = await getContractById(search);
                if (contract) {
                    titleData.setTitleText(`Nhật ký hệ thống - ${contract.code} - ${contract.name}`)
                }
            } else if (objectType === "WaterInvoice") {
                const invoice = await getWaterInvoiceById(search);
                if (invoice) {
                    titleData.setTitleText(`Nhật ký hệ thống - Hóa đơn`)
                }
            } else if (objectType === "WaterIndex") {
                const waterIndex = await getWaterIndexById(search);
                if (waterIndex) {
                    titleData.setTitleText(`Nhật ký hệ thống - Chỉ số`)
                }
            }
        }
    }

    async function onFilterChange(map: Map<string, FilterSearchPayload>, changes: string[]) {
        setFilterMap(new Map(map));
    }

    function onRowSelect(rows: AuditLogModel[]) {
        setSelectedRows([...rows]);
    }


    const showDetail = (record: AuditLogModel) => {
        setModalData(record);
        setDetailModal(true);
    };

    const mapReferenceValue = (record: AuditLogModel) => {
        const fieldName = auditLogRefFieldPipe(record.title);
        if (!fieldName) return "";
        const refDetail = record.auditLogDetails.find((detail) => detail.fieldName === fieldName);
        if (!refDetail) return "";
        return refDetail.newValue;
    }

    return (
    <div className="px-4 py-6">
        {contextHolder}
        <FilterBar setMap={setFilterMap} onChange={onFilterChange} templates={filterTemplates} map={filterMap}/>
        <div className="mt-4 border-t border-dashed border-gray-300 pt-6">
            <DataTable<AuditLogModel>
                columns={[
                    { dataIndex: 'username', name: 'Tài khoản tác động'},
                    { dataIndex: 'title', name: 'Bảng tác động', pipe: (text) => <p style={{fontWeight: "bold", color: "var(--primary-color)"}}>{auditLogTitlePipe(text).toUpperCase()}</p>},
                    { dataIndex: 'recordId', name: 'Mã tham chiếu', },
                    { dataIndex: 'action', name: 'Hành động', pipe: (text) => {
                            let color: string | undefined = undefined;
                            switch (text) {
                                case "Thêm":
                                    color = "var(--success-button)"
                                    break;
                                case "Cập nhật":
                                    color = "var(--primary-button)"
                                    break;
                                case "Xóa":
                                    color = "var(--danger-color)"
                                    break;
                            }
                            return <div className="flex flex-row justify-center items-center">
                                <Tag color={color} style={{minWidth: "80px", textAlign: "center"}}><b>{text}</b></Tag>
                            </div>
                        },
                        width: 100
                    },
                    { dataIndex: 'timestamp', name: 'Thời gian', pipe: formatDateTimePipe, width: 200},
                    {
                        name: 'Thao tác',
                        render: (_, record) => (
                                <div className="flex flex-row px-1 items-center justify-center">
                                    <Tooltip title="Xem chi tiết"><Button type="text" icon={<InfoCircleOutlined />} onClick={() => showDetail(record)}/></Tooltip>
                                </div>
                            ),
                        fixed: "right",
                        width: 100
                    },
                ]}
                repository={auditLogRepo}
                filterMap={filterMap}
                onSelect={onRowSelect}
            ></DataTable>
        </div>
        <DetailModal isModalOpen={isDetailModal} setIsModalOpen={setDetailModal} data={modalData}></DetailModal>
    </div>)
}